import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown } from "lucide-react";
import { TableColumn } from "./data-table";

interface SkeletonTableBodyProps {
  columsNumber: number;
  rowsNumber: number;
  columns?: TableColumn[];
  enableRowSelection?: boolean;
}

const RowSkeleton = ({
  columsNumber,
  enableRowSelection = false,
}: {
  columsNumber: number;
  enableRowSelection?: boolean;
}) => (
  <TableRow>
    {enableRowSelection && (
      <TableCell className="w-10 py-2">
        <Skeleton className="h-4 w-4 rounded" />
      </TableCell>
    )}
    {Array.from({ length: columsNumber }).map((_, index) => (
      <TableCell key={index} className="py-4">
        <Skeleton className="h-5 w-20" />
      </TableCell>
    ))}
    <TableCell className="py-4 text-right">
      <Skeleton className="h-5 w-6 rounded ml-auto" />
    </TableCell>
  </TableRow>
);

/**
 * Skeleton del CUERPO de la tabla (sin Card/header). Se renderiza dentro de la
 * card principal real para mantener el header consistente entre estados.
 */
export default function SkeletonTableBody({
  columsNumber,
  rowsNumber,
  columns,
  enableRowSelection = false,
}: SkeletonTableBodyProps) {
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <div className="inline-block min-w-full align-middle">
        <Table>
          <TableHeader className="rounded-t-lg">
            <TableRow>
              {enableRowSelection && (
                <TableHead className="w-10">
                  <Skeleton className="h-4 w-4 rounded" />
                </TableHead>
              )}
              {columns
                ? columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className={`text-gray-500 dark:text-gray-300 ${column.className ?? ""}`}
                    >
                      <div className="flex items-center gap-2 select-none">
                        <span>{column.label}</span>
                        {column.sortable && (
                          <ArrowUpDown className="w-4 h-4 opacity-50" />
                        )}
                      </div>
                    </TableHead>
                  ))
                : Array.from({ length: columsNumber }).map((_, index) => (
                    <TableHead key={index}>
                      <Skeleton className="h-4 w-20" />
                    </TableHead>
                  ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rowsNumber }).map((_, index) => (
              <RowSkeleton
                key={index}
                columsNumber={columsNumber}
                enableRowSelection={enableRowSelection}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
